import React, { useState, useEffect, useCallback } from 'react';
import { Movie } from '../../types/Movie';
import { movieService } from '../../services/movieService';
import MovieList from './MovieList';
import MovieForm from './MovieForm';
import PageHeader from '../common/PageHeader';
import { User } from '../../services/authService';
import './MoviePage.css';

interface MoviePageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const MoviePage: React.FC<MoviePageProps> = ({ 
  user, 
  onClose,
  onToggleSidebar 
}) => {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMovie, setEditingMovie] = useState<Movie | undefined>();

  // Load movies
  const loadMovies = useCallback(async () => {
    if (!user) {
      console.log('MoviePage: No user, skipping load');
      return;
    }
    
    try {
      console.log('MoviePage: Loading movies for user:', user.id);
      setLoading(true);
      setError(null);
      
      const moviesData = await movieService.getMovies();
      console.log('MoviePage: Fetched movies:', moviesData);
      setMovies(moviesData || []);
    } catch (err: any) {
      console.error('MoviePage: Error loading movies:', err);
      setError(`Failed to load movies: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadMovies();
    } else {
      setMovies([]);
      setLoading(false);
      setError(null);
    }
  }, [user, loadMovies]);

  const handleAddMovie = () => {
    setEditingMovie(undefined);
    setShowForm(true);
  };

  const handleEditMovie = (movie: Movie) => {
    setEditingMovie(movie);
    setShowForm(true);
  };

  const handleSubmitMovie = async (movie: Movie) => {
    if (editingMovie) {
      // Update existing movie
      const success = await movieService.updateMovie(movie);
      if (success) {
        setShowForm(false);
        setEditingMovie(undefined);
        loadMovies();
      }
    } else {
      // Add new movie
      const { id, dateAdded, ...movieData } = movie;
      const newMovie = await movieService.addMovie(movieData);
      if (newMovie) {
        setShowForm(false);
        setEditingMovie(undefined);
        loadMovies();
      }
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingMovie(undefined);
  };

  const handleDeleteMovie = async (movieId: string) => {
    const success = await movieService.deleteMovie(movieId);
    if (success) {
      setMovies(prev => prev.filter(m => m.id !== movieId));
    }
  };

  return (
    <div className="movie-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">🎬 My Movies</h1>}
        rightActions={
          <button className="page-header-icon-btn" onClick={handleAddMovie} title="Add Movie">
            +
          </button>
        }
      />

      <div className="movie-page-content">
        <MovieList
          movies={movies}
          loading={loading}
          error={error}
          user={user}
          onEditMovie={handleEditMovie}
          onDeleteMovie={handleDeleteMovie}
        />
      </div>

      {showForm && (
        <MovieForm
          movie={editingMovie}
          onSubmit={handleSubmitMovie}
          onCancel={handleCancelForm}
        />
      )}
    </div>
  );
};

export default MoviePage;

